export type VisibilityPauseCallbacks = {
  onPause: () => void;
  onResume: () => void;
};

/**
 * Inscreve pause/resume em visibilitychange. Retorna cleanup.
 * Para canais auxiliares (ex.: lead-tags) — o hub workspace tem lógica própria.
 */
export function subscribeVisibilityPause(
  enabled: boolean,
  callbacks: VisibilityPauseCallbacks,
  hideDelayMs = 0,
): () => void {
  if (!enabled || typeof document === "undefined") {
    return () => {};
  }

  let timer: ReturnType<typeof setTimeout> | null = null;
  let paused = false;

  const onVisibilityChange = () => {
    if (document.hidden) {
      if (paused || timer) return;
      if (hideDelayMs <= 0) {
        paused = true;
        callbacks.onPause();
        return;
      }
      timer = setTimeout(() => {
        timer = null;
        if (typeof document !== "undefined" && !document.hidden) return;
        paused = true;
        callbacks.onPause();
      }, hideDelayMs);
      return;
    }

    if (timer) {
      clearTimeout(timer);
      timer = null;
      return;
    }

    if (paused) {
      paused = false;
      callbacks.onResume();
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  return () => {
    if (timer) clearTimeout(timer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}

export type RealtimeVisibilityPauseBindOptions = {
  pauseEnabled: boolean;
  /** Inscreve canal; deve ignorar se document.hidden e pauseEnabled */
  subscribe: () => void;
  teardown: () => void;
  /** Reconcile debounced ao retornar à aba (ex.: refetch / invalidate) */
  onReconcile: () => void;
};

/**
 * Liga subscribe/teardown de um canal Supabase ao pause-on-hidden.
 * Um único caminho de reconcile: onResume (flag=1) ou visibilitychange (flag=0).
 */
export function bindRealtimeVisibilityPause(options: RealtimeVisibilityPauseBindOptions): () => void {
  const { pauseEnabled, subscribe, teardown, onReconcile } = options;

  const reconcileFromBackground = () => {
    if (typeof document !== "undefined" && document.hidden) return;
    subscribe();
    onReconcile();
  };

  subscribe();

  const unsubscribeVisibilityPause = subscribeVisibilityPause(pauseEnabled, {
    onPause: teardown,
    onResume: () => {
      if (pauseEnabled) {
        reconcileFromBackground();
      } else {
        subscribe();
      }
    },
  });

  let removeVisibilityReconcile: (() => void) | undefined;
  if (!pauseEnabled && typeof document !== "undefined") {
    const onVisibilityChange = () => {
      if (!document.hidden) reconcileFromBackground();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    removeVisibilityReconcile = () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }

  return () => {
    unsubscribeVisibilityPause();
    removeVisibilityReconcile?.();
    teardown();
  };
}

export function isRealtimePausedByVisibility(pauseEnabled: boolean): boolean {
  return pauseEnabled && typeof document !== "undefined" && document.hidden;
}
